import React, { useState, useEffect } from 'react';
import { get } from '../../ApiServices/ApiServices';

const AccountOverviewTable = () => {
    // State for all order types
    const [orderData, setOrderData] = useState({
        wedding: null,
        single: null,
        prom: null
    });

    // Wedding row state - only month number
    const [weddingMonth, setWeddingMonth] = useState(new Date().getMonth() + 1);
    const [weddingLoading, setWeddingLoading] = useState(false);

    // Single row state - only month number
    const [singleMonth, setSingleMonth] = useState(new Date().getMonth() + 1);
    const [singleLoading, setSingleLoading] = useState(false);

    // Prom row state - only month number
    const [promMonth, setPromMonth] = useState(new Date().getMonth() + 1);
    const [promLoading, setPromLoading] = useState(false);

    // Current year
    const currentYear = new Date().getFullYear();

    // Month options - only current year months
    const months = [
        { value: 1, label: 'January' }, { value: 2, label: 'February' },
        { value: 3, label: 'March' }, { value: 4, label: 'April' },
        { value: 5, label: 'May' }, { value: 6, label: 'June' },
        { value: 7, label: 'July' }, { value: 8, label: 'August' },
        { value: 9, label: 'September' }, { value: 10, label: 'October' },
        { value: 11, label: 'November' }, { value: 12, label: 'December' }
    ];

    // ============== API Functions ==============
    const fetchWeddingData = async () => {
        try {
            setWeddingLoading(true);
            const response = await get(`account-overview?wedding_month=${weddingMonth}`);
            if (response?.status) {
                setOrderData(prev => ({ ...prev, wedding: response.data }));
            }
        } catch (err) {
            if (err?.message?.includes("Invalid")) {
                localStorage.removeItem('token');
                window.location.href = '/login';
            }
        } finally {
            setWeddingLoading(false);
        }
    };

    const fetchSingleData = async () => {
        try {
            setSingleLoading(true);
            const response = await get(`account-overview?single_month=${singleMonth}`);
            if (response?.status) {
                setOrderData(prev => ({ ...prev, single: response.data }));
            }
        } catch (err) {
            if (err?.message?.includes("Invalid")) {
                localStorage.removeItem('token');
                window.location.href = '/login';
            }
        } finally {
            setSingleLoading(false);
        }
    };

    const fetchPromData = async () => {
        try {
            setPromLoading(true);
            const response = await get(`account-overview?prom_month=${promMonth}`);
            if (response?.status) {
                setOrderData(prev => ({ ...prev, prom: response.data }));
            }
        } catch (err) {
            if (err?.message?.includes("Invalid")) {
                localStorage.removeItem('token');
                window.location.href = '/login';
            }
        } finally {
            setPromLoading(false);
        }
    };

    // ============== Effect Functions ==============
    useEffect(() => {
        fetchWeddingData();
    }, [weddingMonth]);

    useEffect(() => {
        fetchSingleData();
    }, [singleMonth]);

    useEffect(() => {
        fetchPromData();
    }, [promMonth]);

    // ============== UI Render Functions ==============
    const renderMonthDropdown = (month, onChange) => (
        <select
            className="form-select form-select-sm"
            style={{
                width: '200px',
                border: '2px solid #d0d5dd',
                borderRadius: '8px',
                padding: '0.5rem 1rem',
                backgroundColor: '#ffffff',
                color: '#101828',
                fontWeight: '500',
                fontSize: '0.95rem'
            }}
            value={month}
            onChange={(e) => onChange(parseInt(e.target.value))}
        >
            {months.map(m => (
                <option key={m.value} value={m.value}>
                    {m.label} {currentYear}
                </option>
            ))}
        </select>
    );
    const renderWeddingRow = () => (
        <tr>
            <th scope="row">
                {renderMonthDropdown(weddingMonth, setWeddingMonth)}
            </th>
            <td>Wedding</td>
            {weddingLoading ? (
                <td colSpan="8" className="text-center">
                    <div className="spinner-border spinner-border-sm text-primary" />
                </td>
            ) : (
                <>
                    <td>{orderData.wedding?.wedding_order_current_month || 0}</td>
                    <td>{orderData.wedding?.wedding_order_previous_month || 0}</td>
                    <td>{orderData.wedding?.wedding_order_per_change_from_prev_month || 0}%</td>
                    <td>{orderData.wedding?.wedding_order_same_month_last_year || 0}</td>
                    <td>{orderData.wedding?.wedding_order_per_change_yoy || 0}%</td>
                    <td>{orderData.wedding?.wedding_order_ytd_rentals || 0}</td>
                    <td>{orderData.wedding?.wedding_order_previous_ytd_rentals || 0}</td>
                    <td>{orderData.wedding?.wedding_order_ytd_per_change || 0}%</td>
                </>
            )}
        </tr>
    );

    const renderSingleRow = () => (
        <tr>
            <th scope="row">
                {renderMonthDropdown(singleMonth, setSingleMonth)}
            </th>
            <td>Single</td>
            {singleLoading ? (
                <td colSpan="8" className="text-center">
                    <div className="spinner-border spinner-border-sm text-primary" />
                </td>
            ) : (
                <>
                    <td>{orderData.single?.single_order_current_month || 0}</td>
                    <td>{orderData.single?.single_order_previous_month || 0}</td>
                    <td>{orderData.single?.single_order_per_change_from_prev_month || 0}%</td>
                    <td>{orderData.single?.single_order_same_month_last_year || 0}</td>
                    <td>{orderData.single?.single_order_per_change_yoy || 0}%</td>
                    <td>{orderData.single?.single_order_ytd_rentals || 0}</td>
                    <td>{orderData.single?.single_order_previous_ytd_rentals || 0}</td>
                    <td>{orderData.single?.single_order_ytd_per_change || 0}%</td>
                </>
            )}
        </tr>
    );

    const renderPromRow = () => (
        <tr>
            <th scope="row">
                {renderMonthDropdown(promMonth, setPromMonth)}
            </th>
            <td>Prom</td>
            {promLoading ? (
                <td colSpan="8" className="text-center">
                    <div className="spinner-border spinner-border-sm text-primary" />
                </td>
            ) : (
                <>
                    <td>{orderData.prom?.prom_order_current_month || 0}</td>
                    <td>{orderData.prom?.prom_order_previous_month || 0}</td>
                    <td>{orderData.prom?.prom_order_per_change_from_prev_month || 0}%</td>
                    <td>{orderData.prom?.prom_order_same_month_last_year || 0}</td>
                    <td>{orderData.prom?.prom_order_per_change_yoy || 0}%</td>
                    <td>{orderData.prom?.prom_order_ytd_rentals || 0}</td>
                    <td>{orderData.prom?.prom_order_previous_ytd_rentals || 0}</td>
                    <td>{orderData.prom?.prom_order_ytd_per_change || 0}%</td>
                </>
            )}
        </tr>
    );

    const renderTableHeader = () => (
        <thead>
            <tr>
                <th scope="col">Month/Year</th>
                <th scope="col">Order Type</th>
                <th scope="col">Current Month Rentals</th>
                <th scope="col">Previous Month Rentals</th>
                <th scope="col">% Change from Prev Month</th>
                <th scope="col">Same Month Last Year Rentals</th>
                <th scope="col">% Change YoY</th>
                <th scope="col">YTD Rentals</th>
                <th scope="col">Previous YTD Rentals</th>
                <th scope="col">% YTD Change</th>
            </tr>
        </thead>
    );

    // ============== Main Render ==============
    return (
        <>
            <div className="row align-items-center justify-content-between">
                <div className="col-md-6">
                    <h5>Account Overview</h5>
                </div>
            </div>

            <div className="row mt-3">
                <div className="col-md-12">
                    <div className="table-responsive">
                        <table className="table table-striped">
                            {renderTableHeader()}
                            <tbody>
                                {renderWeddingRow()}
                                {renderSingleRow()}
                                {renderPromRow()}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </>);
};

export default AccountOverviewTable;